import React, { useState, useEffect } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, TextField, Button, Grid, Alert,
  CircularProgress, Slider, Divider, InputAdornment,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon     from "@mui/icons-material/Save";
import { isAutheticated } from "../auth/helper";
import { getSettings, updateSettings } from "./helper/adminapicall";

const FeedSettings = () => {
  const { admin, token } = isAutheticated();
  const [form,    setForm]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState({ text:"", severity:"success" });

  const flash = (text, severity="success") => { setMsg({text,severity}); setTimeout(()=>setMsg({text:""}),4000); };
  const set   = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    getSettings(admin._id, token).then(d => {
      if (!d?.error) setForm(d);
      setLoading(false);
    });
  }, [admin._id, token]);

  const handleSave = async () => {
    setSaving(true);
    const d = await updateSettings(admin._id, token, {
      global_poll_interval_seconds: Number(form.global_poll_interval_seconds),
      max_concurrent_fetches:       Number(form.max_concurrent_fetches),
      min_domain_gap_ms:            Number(form.min_domain_gap_ms),
      bge_similarity_threshold:     Number(form.bge_similarity_threshold),
      bge_embedding_ttl_hours:      Number(form.bge_embedding_ttl_hours),
      bge_window_size:              Number(form.bge_window_size),
      url_dedup_days:               Number(form.url_dedup_days || 90),
      bge_url:                      form.bge_url,
      max_article_age_hours:        Number(form.max_article_age_hours),
    });
    setSaving(false);
    if (d?.error) return flash(d.error, "error");
    flash("Settings saved — worker reloads automatically");
  };

  if (loading) return <Base><Box sx={{p:4,textAlign:"center"}}><CircularProgress/></Box></Base>;

  return (
    <Base>
      {msg.text && <Alert severity={msg.severity} sx={{mb:2}} onClose={()=>setMsg({text:""})}>{msg.text}</Alert>}

      <Box sx={{ mb:3, display:"flex", alignItems:"center", gap:1.5 }}>
        <SettingsIcon sx={{ color:"text.secondary" }}/>
        <Box>
          <Typography variant="h5">Feed Ingestion Settings</Typography>
          <Typography variant="body2" color="text.secondary">Global defaults for polling, concurrency, and deduplication</Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Polling */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p:3, border:"1px solid", borderColor:"divider", borderRadius:2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{mb:2}}>Polling</Typography>
            <Box sx={{mb:3}}>
              <Typography variant="body2" gutterBottom>
                Global poll interval: <b>{Math.round(form.global_poll_interval_seconds/60)} min</b>
              </Typography>
              <Slider value={form.global_poll_interval_seconds} min={60} max={7200} step={60}
                onChange={(_,v)=>set("global_poll_interval_seconds",v)}
                marks={[{value:300,label:"5m"},{value:900,label:"15m"},{value:1800,label:"30m"},{value:3600,label:"1h"}]}
                sx={{mb:1}}/>
              <Typography variant="caption" color="text.secondary">
                Applies to all feeds with mode = "Global". Per-group and per-feed intervals override this.
              </Typography>
            </Box>
            <Divider sx={{mb:2}}/>
            <Box sx={{mb:2}}>
              <Typography variant="body2" gutterBottom>
                Max concurrent fetches: <b>{form.max_concurrent_fetches}</b>
              </Typography>
              <Slider value={form.max_concurrent_fetches} min={1} max={200} step={5}
                onChange={(_,v)=>set("max_concurrent_fetches",v)}
                marks={[{value:10,label:"10"},{value:50,label:"50"},{value:100,label:"100"},{value:200,label:"200"}]}/>
              <Typography variant="caption" color="text.secondary">
                Number of feeds fetched simultaneously by the worker.
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom>
                Min gap between requests to the same domain: <b>{form.min_domain_gap_ms}ms</b>
              </Typography>
              <Slider value={form.min_domain_gap_ms} min={0} max={10000} step={500}
                onChange={(_,v)=>set("min_domain_gap_ms",v)}
                marks={[{value:0,label:"0"},{value:2000,label:"2s"},{value:5000,label:"5s"},{value:10000,label:"10s"}]}/>
            </Box>
            <Divider sx={{mb:2, mt:1}}/>
            <Box>
              <Typography variant="body2" gutterBottom>Max article age to ingest</Typography>
              <Box sx={{ display:"flex", alignItems:"center", gap:2, mb:1 }}>
                <TextField
                  size="small" type="number" label="Hours"
                  value={form.max_article_age_hours}
                  inputProps={{ min:1, max:8760 }}
                  onChange={e => set("max_article_age_hours", Math.max(1, Number(e.target.value)))}
                  sx={{ width:110 }}
                />
                <Typography variant="body2" color="text.secondary">
                  ={" "}
                  {form.max_article_age_hours >= 168
                    ? `${+(form.max_article_age_hours / 168).toFixed(1)} week${form.max_article_age_hours !== 168 ? "s" : ""}`
                    : form.max_article_age_hours >= 24
                      ? `${+(form.max_article_age_hours / 24).toFixed(1)} day${form.max_article_age_hours !== 24 ? "s" : ""}`
                      : `${form.max_article_age_hours} hour${form.max_article_age_hours !== 1 ? "s" : ""}`}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Articles published before this cutoff are silently skipped during ingestion.
                Default is 48 hours (2 days) — prevents importing large backlogs from newly added feeds.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* BGE / Dedup */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p:3, border:"1px solid", borderColor:"divider", borderRadius:2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{mb:2}}>Semantic Deduplication (BGE)</Typography>
            <Box sx={{mb:3}}>
              <Typography variant="body2" gutterBottom>
                Similarity threshold: <b>{form.bge_similarity_threshold}</b>
              </Typography>
              <Slider value={form.bge_similarity_threshold} min={0.5} max={0.99} step={0.01}
                onChange={(_,v)=>set("bge_similarity_threshold",v)}
                marks={[{value:0.65,label:"0.65"},{value:0.72,label:"0.72"},{value:0.85,label:"0.85"},{value:0.95,label:"0.95"}]}/>
              <Typography variant="caption" color="text.secondary">
                Articles with cosine similarity above this threshold are treated as duplicates. Lower = more aggressive dedup.
              </Typography>
            </Box>
            <Divider sx={{mb:2}}/>
            <TextField label="Embedding TTL (hours)" size="small" type="number" fullWidth sx={{mb:2}}
              value={form.bge_embedding_ttl_hours} inputProps={{min:1}}
              onChange={e=>set("bge_embedding_ttl_hours",Math.max(1,Number(e.target.value)))}
              helperText="Embeddings older than this are ignored for similarity checks"/>
            <Divider sx={{mb:2}}/>
            <Box sx={{mb:2}}>
              <Typography variant="body2" gutterBottom>
                Dedup window size: <b>{(form.bge_window_size || 5000).toLocaleString()} articles</b>
              </Typography>
              <Slider value={form.bge_window_size || 5000} min={500} max={20000} step={500}
                onChange={(_,v)=>set("bge_window_size",v)}
                marks={[
                  {value:1000, label:"1k"},
                  {value:5000, label:"5k"},
                  {value:10000,label:"10k"},
                  {value:20000,label:"20k"},
                ]}/>
              <Typography variant="caption" color="text.secondary">
                How many recent articles to compare against. Higher = better dedup coverage at high volume, ~1ms per 1000.
              </Typography>
            </Box>
            <Divider sx={{mb:2}}/>
            <Box sx={{mb:2}}>
              <Typography variant="body2" gutterBottom>
                URL dedup window:{" "}
                <b>
                  {(form.url_dedup_days || 90) >= 365
                    ? "1 year"
                    : (form.url_dedup_days || 90) >= 30
                      ? `${Math.round((form.url_dedup_days || 90) / 30)} month${(form.url_dedup_days || 90) >= 60 ? "s" : ""}`
                      : `${form.url_dedup_days || 90} days`}
                </b>
              </Typography>
              <Slider
                value={form.url_dedup_days || 90}
                min={7} max={365} step={null}
                onChange={(_,v) => set("url_dedup_days", v)}
                valueLabelDisplay="auto"
                valueLabelFormat={v => v >= 365 ? "1yr" : v >= 30 ? `${Math.round(v/30)}mo` : `${v}d`}
                marks={[
                  {value: 7,   label: "7d"},
                  {value: 30,  label: "1mo"},
                  {value: 90,  label: "3mo"},
                  {value: 180, label: "6mo"},
                  {value: 365, label: "1yr"},
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                How far back to check for duplicate URLs. Articles with the same URL fetched within
                this window are blocked. Independent of article retention (purge).
              </Typography>
            </Box>
            <TextField label="BGE container URL" size="small" fullWidth
              value={form.bge_url} onChange={e=>set("bge_url",e.target.value)}
              helperText="HTTP endpoint of the embedding service. Changes here notify the worker automatically."/>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt:3 }}>
        <Button variant="contained" size="large" startIcon={<SaveIcon/>} onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20}/> : "Save Settings"}
        </Button>
      </Box>
    </Base>
  );
};

export default FeedSettings;
